<template>
  <transition
    enter-active-class="animated slideInRight"
    leave-active-class="animated zoomOut"
  >
    <v-flex
      v-if="boxIsLoading"
      xs12
      md12
      lg4
    >
      <v-card>
        <v-toolbar color="secondary" dark tabs>
          <v-toolbar-title>{{$t("general.stats")}}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            @click="resetActiveBoxStats"
            flat
            icon
          >
            <v-icon>clear</v-icon>
          </v-btn>
        </v-toolbar>
        <loading-overlay
          :color="'accent'"
          :size="70"
          :width="5"
        ></loading-overlay>
      </v-card>
    </v-flex>
    <v-flex
      xs4
      v-if="Object.keys(activeBoxStats).length !== 0 && activeBoxStats.constructor === Object"
    >
      <v-card>
        <v-toolbar color="secondary" dark tabs>
          <v-toolbar-title>
            <v-tooltip right>
              <span slot="activator">{{$t("general.statsOf", [activeBoxStats.box_name])}}</span>
              <span>{{ activeBoxStats.box_note }} (id:{{ activeBoxStats.id }})</span>
            </v-tooltip>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            @click="resetActiveBoxStats"
            flat
            icon
          >
            <v-icon>clear</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-layout
            v-show="!boxIsLoading"
            row
            v-viewer
          >
            <v-flex>
              <img class="display-preview" :src="'data:image/jpeg;base64, ' + activeBoxStats.image">
            </v-flex>
          </v-layout>
          <v-list
            v-show="!boxIsLoading"
            two-line
            subheader
            dense
          >
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("box.osVersion")}}</v-list-tile-title>
                <v-list-tile-sub-title> {{activeBoxStats.os_version}}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("general.name")}}</v-list-tile-title>
                <v-list-tile-sub-title> {{activeBoxStats.name}}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("general.tarsiusPlayerVersion")}}</v-list-tile-title>
                <v-list-tile-sub-title>
                  {{ activeBoxStats.tarsius_player_version }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile v-if="activeBoxStats.playlist_name" avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("general.currentlyPlaying")}}</v-list-tile-title>
                <v-list-tile-sub-title><v-tooltip right>
                  <span slot="activator"> {{ activeBoxStats.playlist_name }}</span>
                  <span>(id:{{ activeBoxStats.playlist_id }})</span>
                </v-tooltip></v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("general.ipAddress")}}</v-list-tile-title>
                <v-list-tile-sub-title> {{activeBoxStats.ip_address}}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("general.macAddress")}}</v-list-tile-title>
                <v-list-tile-sub-title> {{activeBoxStats.mac_address}}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("box.boxDateTime")}}</v-list-tile-title>
                <v-list-tile-sub-title> {{activeBoxStats.date}}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("box.boxUptime")}}</v-list-tile-title>
                <v-list-tile-sub-title> {{activeBoxStats.uptime}}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("box.displayResolution")}}</v-list-tile-title>
                <v-list-tile-sub-title>
                  {{activeBoxStats.display_resolution}}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("box.volume")}}</v-list-tile-title>
                <v-list-tile-sub-title> {{activeBoxStats.volume}}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("box.boxStorageSpace")}}</v-list-tile-title>
                <v-list-tile-sub-title>
                  Internal (free/total): {{activeBoxStats.storage_space_internal}}
                </v-list-tile-sub-title>
                <v-list-tile-sub-title>
                  External (free/total): {{activeBoxStats.storage_space_external}}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{$t("box.boxRamUsage")}}</v-list-tile-title>
                <v-list-tile-sub-title>
                  (free/total): {{activeBoxStats.ram_usage}}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-card-text>
      </v-card>
    </v-flex>
  </transition>
</template>

<script>
import Vue from 'vue';
import Viewer from 'v-viewer';
import { RESET_ACTIVE_BOX_STATS } from '@/store/actions/boxes';
import LoadingOverlay from '@/components/generic/LoadingOverlay.vue';

Vue.use(Viewer);

export default {
  components: {
    LoadingOverlay,
  },
  computed: {
    activeBoxStats: {
      get() {
        return this.$store.getters.getActiveBoxStats;
      },
    },
    boxIsLoading: {
      get() {
        return this.$store.getters.getBoxIsLoading;
      },
    },
  },
  data() {
    return {
      currentBox: {},
      apiUrl: window.api_url,
    };
  },
  methods: {
    resetActiveBoxStats() {
      this.$store.commit(RESET_ACTIVE_BOX_STATS);
    },
  },
};
</script>

<style>
.display-preview {
  width: 100%;
}

.display-preview:hover {
  cursor: pointer;
}

</style>
